export const TILE_LETTERS = ["A", "B", "C", "D"] as const;

interface AnswerTileProps {
  index: number;
  text: string;
  onClick?: () => void;
  selected?: boolean;
  dimmed?: boolean;
  correct?: boolean | null;
  disabled?: boolean;
}

// Tiles are deliberately colour-neutral until the reveal. The old
// Kahoot-style per-index colours made every True/False question look
// pre-answered (A = teal → "True is right", B = coral → "False is wrong")
// — teacher feedback 2026-08-27. After QUESTION_END the host screen
// passes `correct`, and only then do tiles turn green (correct) / red.
export function AnswerTile({
  index,
  text,
  onClick,
  selected,
  dimmed,
  correct,
  disabled,
}: AnswerTileProps) {
  const letter = TILE_LETTERS[index % TILE_LETTERS.length] ?? "?";

  let surface = "bg-white text-brand-ink";
  let badge = "bg-brand-ink/10 text-brand-ink";
  let state = "";
  if (correct === true) {
    surface = "bg-brand-good text-white";
    badge = "bg-black/20 text-white";
    state = "ring-4 ring-white/70 scale-[1.02]";
  } else if (correct === false) {
    surface = "bg-brand-bad text-white";
    badge = "bg-black/20 text-white";
    state = "opacity-60";
  } else if (selected) {
    state = "ring-4 ring-offset-2 ring-offset-brand-ink ring-brand-blue";
  } else if (dimmed) {
    state = "opacity-40";
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`relative w-full text-left p-5 md:p-6 rounded-2xl ${surface} font-semibold text-lg md:text-xl transition-all duration-200 ${state} ${
        disabled ? "cursor-default" : "hover:brightness-95 active:scale-[.99]"
      }`}
    >
      <div className="flex items-center gap-4">
        <span
          className={`w-9 h-9 rounded-lg ${badge} inline-flex items-center justify-center font-bold flex-shrink-0`}
        >
          {letter}
        </span>
        <span className="flex-1 leading-snug">{text}</span>
      </div>
    </button>
  );
}
